/* ==========================================================================
   Everest Beds — Premium design system (EB) behaviours
   Custom elements re-initialise automatically when the theme editor
   re-renders a section, so nothing needs manual re-binding.
   ========================================================================== */
(() => {
  if (window.EB) return;

  const root = document.documentElement;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (window.Shopify && Shopify.designMode) root.classList.add('shopify-design-mode');

  /* ---------- Scroll reveals ---------- */
  let io = null;
  const reveal = (scope = document) => {
    const els = scope.querySelectorAll('[data-eb-rv]:not(.is-in), [data-eb-lines]:not(.is-in)');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window) || !root.classList.contains('eb-motion')) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    if (!io) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    els.forEach((el) => io.observe(el));
  };

  /* ---------- Count-up numbers ---------- */
  let cio = null;
  const formatNum = (n, dec, locale) =>
    n.toLocaleString(locale || 'en-GB', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const countUp = (el) => {
    const end = parseFloat(el.dataset.ebCount);
    const dec = parseInt(el.dataset.dec || '0', 10);
    const pre = el.dataset.prefix || '';
    const suf = el.dataset.suffix || '';
    if (Number.isNaN(end)) return;
    if (reduceMotion) { el.textContent = pre + formatNum(end, dec) + suf; return; }
    let t0 = null;
    const dur = 1500;
    const step = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min(1, (ts - t0) / dur);
      const e = 1 - Math.pow(1 - p, 4);
      el.textContent = pre + formatNum(end * e, dec) + suf;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  const counters = (scope = document) => {
    const els = scope.querySelectorAll('[data-eb-count]:not([data-counted])');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(countUp); return; }
    if (!cio) {
      cio = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { countUp(e.target); cio.unobserve(e.target); }
        });
      }, { threshold: 0.6 });
    }
    els.forEach((el) => { el.dataset.counted = '1'; cio.observe(el); });
  };

  /* ---------- Magnetic buttons (fine pointers only) ---------- */
  const magnetic = (scope = document) => {
    if (!finePointer || reduceMotion || !root.classList.contains('eb-motion')) return;
    scope.querySelectorAll('[data-eb-magnetic]:not([data-mag-bound])').forEach((el) => {
      el.dataset.magBound = '1';
      const pull = parseFloat(el.dataset.ebMagnetic || '0.22');
      let raf = null;
      el.addEventListener('pointermove', (ev) => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          const r = el.getBoundingClientRect();
          const x = (ev.clientX - r.left - r.width / 2) * pull;
          const y = (ev.clientY - r.top - r.height / 2) * pull;
          el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
        });
      });
      el.addEventListener('pointerleave', () => { el.style.transform = ''; });
    });
  };

  /* ---------- Word-by-word heading reveal ---------- */
  const splitWords = (scope = document) => {
    if (reduceMotion || !root.classList.contains('eb-words') || !root.classList.contains('eb-motion')) return;
    scope.querySelectorAll('.eb .eb-h2[data-eb-rv]:not([data-split]), .eb [data-eb-split]:not([data-split])').forEach((h) => {
      h.dataset.split = '1';
      let i = 0;
      const walk = (node) => {
        [...node.childNodes].forEach((child) => {
          if (child.nodeType === 3) {
            const parts = child.textContent.split(/(\s+)/);
            if (parts.every((p) => !p.trim())) return;
            const frag = document.createDocumentFragment();
            parts.forEach((part) => {
              if (!part) return;
              if (!part.trim()) { frag.appendChild(document.createTextNode(part)); return; }
              const w = document.createElement('span');
              w.className = 'eb-w';
              w.style.setProperty('--wi', i++);
              w.textContent = part;
              frag.appendChild(w);
            });
            child.replaceWith(frag);
          } else if (child.nodeType === 1 && !child.classList.contains('eb-w')) walk(child);
        });
      };
      walk(h);
      h.setAttribute('aria-label', h.textContent.replace(/\s+/g, ' ').trim());
      h.setAttribute('data-eb-rv', 'words');
    });
  };

  /* ---------- Cursor spotlight ---------- */
  if (finePointer) {
    let spotRaf = null; let spotEv = null;
    document.addEventListener('pointermove', (ev) => {
      spotEv = ev;
      if (spotRaf) return;
      spotRaf = requestAnimationFrame(() => {
        spotRaf = null;
        const el = spotEv.target.closest && spotEv.target.closest('[data-eb-spotlight]');
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${spotEv.clientX - r.left}px`);
        el.style.setProperty('--my', `${spotEv.clientY - r.top}px`);
      });
    }, { passive: true });
  }

  /* ---------- Product description: enhance in place (no template changes) ---------- */
  function enhanceDescriptions(scope = document) {
    scope.querySelectorAll('.cc-product-details .product-description').forEach((d) => {
      if (d.closest('eb-desc')) return;
      // A tab bar with only "Description" in it becomes an editorial heading.
      const tabbed = d.closest('tabbed-content');
      if (tabbed && !tabbed.previousElementSibling?.classList.contains('eb-desc-head')) {
        const tabs = tabbed.querySelectorAll('[role="tab"]');
        const list = tabbed.querySelector('.tablist');
        if (tabs.length === 1 && list) {
          const head = document.createElement('div');
          head.className = 'eb-desc-head';
          const eyebrow = document.createElement('p');
          eyebrow.className = 'eb-desc-head__eyebrow';
          eyebrow.textContent = 'The details';
          head.appendChild(eyebrow);
          const title = (document.querySelector('.product-info .product-title, .product-title') || {}).textContent;
          if (title && title.trim()) {
            const h = document.createElement('h2');
            h.className = 'eb-desc-head__title';
            h.textContent = `About the ${title.trim()}`;
            head.appendChild(h);
          }
          list.hidden = true;
          tabbed.before(head);
        }
      }
      const mark = document.createComment('');
      d.before(mark);
      const wrap = document.createElement('eb-desc');
      wrap.appendChild(d);
      mark.replaceWith(wrap);
    });
  }

  /* ---------- Product page extras: "You save" pill + jump navigation ---------- */
  function pdpExtras() {
    const main = document.querySelector('.cc-main-product');
    if (!main || main.dataset.ebExtras) return;
    main.dataset.ebExtras = '1';

    // 1. "You save £X" next to the price, kept in sync when the price changes.
    const priceBox = main.querySelector('.product-info__price');
    if (priceBox) {
      const money = (el) => {
        if (!el) return NaN;
        const m = el.textContent.replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
        return m ? parseFloat(m[1]) : NaN;
      };
      const sym = (el) => ((el && el.textContent.match(/[£$€]/)) || ['£'])[0];
      const pill = document.createElement('span');
      pill.className = 'eb-save';
      pill.hidden = true;
      const update = () => {
        const now = priceBox.querySelector('.price__current');
        const was = priceBox.querySelector('.price__was');
        const a = money(now); const b = money(was);
        const visible = was && was.offsetParent !== null;
        if (visible && b > a && a > 0) {
          const diff = b - a;
          const txt = `You save ${sym(now)}${diff.toFixed(diff % 1 ? 2 : 0)} (${Math.round((diff / b) * 100)}%)`;
          if (pill.textContent !== txt) pill.textContent = txt;
          pill.hidden = false;
        } else pill.hidden = true;
      };
      const host = priceBox.querySelector('.price__default') || priceBox;
      host.appendChild(pill);
      update();
      let raf = null;
      new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(() => { raf = null; update(); }); })
        .observe(priceBox, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
    }

    // 2. Jump navigation to the parts of the page below the product (desktop).
    if (!finePointer || !('IntersectionObserver' in window)) return;
    const pick = (sel, label) => { const el = document.querySelector(sel); return el && el.offsetHeight > 40 ? { el, label } : null; };
    const reviewApps = [...document.querySelectorAll('main .shopify-section.cc-apps')].find((sec) => sec.querySelector('[class*="jdgm"], [class*="trustview"], [id*="review"], .shopify-app-block') && sec.offsetHeight > 120);
    const targets = [
      pick('main .cc-product-details', 'Details'),
      pick('main .eb-why-choose', 'Why us'),
      reviewApps ? { el: reviewApps, label: 'Reviews' } : null,
      pick('main .gallery-slideshow', 'Real homes'),
      pick('main .cc-product-recommendations', 'More beds'),
    ].filter(Boolean);
    if (targets.length < 2) return;
    const nav = document.createElement('nav');
    nav.className = 'eb-jump';
    nav.setAttribute('aria-label', 'On this page');
    const top = document.createElement('button');
    top.type = 'button'; top.className = 'eb-jump__top'; top.setAttribute('aria-label', 'Back to top');
    top.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
    top.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }));
    nav.appendChild(top);
    const links = targets.map((t) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'eb-jump__link'; b.textContent = t.label;
      b.addEventListener('click', () => {
        const y = t.el.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top: y, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      nav.appendChild(b);
      return b;
    });
    document.body.appendChild(nav);
    // Show once the buy area is out of view; hide again at the very top.
    new IntersectionObserver((en) => en.forEach((e) => nav.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.top < 0)), { threshold: 0 })
      .observe(main);
    // Highlight the part currently on screen.
    const seen = new Map();
    const io = new IntersectionObserver((en) => {
      en.forEach((e) => seen.set(e.target, e.isIntersecting ? e.intersectionRatio : 0));
      let best = -1; let bestRatio = 0;
      targets.forEach((t, i) => { const r = seen.get(t.el) || 0; if (r > bestRatio) { bestRatio = r; best = i; } });
      links.forEach((l, i) => l.classList.toggle('is-active', i === best));
    }, { threshold: [0, .1, .25, .5, .75] });
    targets.forEach((t) => io.observe(t.el));
  }

  const init = (scope) => { splitWords(scope); reveal(scope); counters(scope); magnetic(scope); try { enhanceDescriptions(scope); } catch (e) { /* leave description as is */ } if (scope === undefined || scope === document) { const run = () => { try { pdpExtras(); } catch (e) { /* optional extras */ } }; if (document.readyState === 'complete') run(); else window.addEventListener('load', run, { once: true }); } };
  window.EB = { init, reveal, reduceMotion, finePointer };

  /* ---------- Inertia smooth scrolling (desktop pointers only) ---------- */
  const startSmooth = () => {
    if (!root.classList.contains('eb-smooth') || !window.Lenis || !finePointer || reduceMotion || root.classList.contains('shopify-design-mode')) return;
    const lenis = new window.Lenis({
      autoRaf: true,
      lerp: 0.095,
      wheelMultiplier: 1,
      anchors: true,
      allowNestedScroll: true,
      prevent: (node) => !!(node.closest && node.closest('.drawer, dialog, [role="dialog"], .modal, .main-nav__child, .main-menu__content, predictive-search, .predictive-search, .custom-select__listbox, [data-lenis-prevent]')),
    });
    EB.lenis = lenis;
    // Theme drawers / menus lock the page with body.fixed or overflow-hidden — pause while open.
    const sync = () => {
      const locked = document.body.classList.contains('fixed') || document.body.classList.contains('overflow-hidden') || getComputedStyle(document.body).overflow === 'hidden';
      if (locked) lenis.stop(); else lenis.start();
    };
    new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
    new MutationObserver(sync).observe(root, { attributes: true, attributeFilter: ['class', 'style'] });
  };

  /* ---------- Product-card hover image: download on first hover only ---------- */
  if (finePointer) {
    document.addEventListener('pointerover', (e) => {
      const card = e.target.closest && e.target.closest('.eb-pcard');
      const tpl = card && card.querySelector('.eb-pcard__alt > template');
      if (tpl) tpl.replaceWith(tpl.content.cloneNode(true));
    }, { passive: true });
  }

  const boot = () => { init(); startSmooth(); };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:select', (e) => init(e.target));

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* ---------- <eb-slider>: snap rail + arrows + progress + drag ---------- */
  define('eb-slider', class extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.eb-rail');
      if (!this.rail) return;
      this.prev = this.querySelector('[data-eb-prev]');
      this.next = this.querySelector('[data-eb-next]');
      this.bar = this.querySelector('.eb-progress > i');
      this.onScroll = () => { if (!this.ticking) { this.ticking = true; requestAnimationFrame(() => { this.ticking = false; this.update(); }); } };
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.ro = new ResizeObserver(() => this.update());
      this.ro.observe(this.rail);
      if (finePointer) this.drag();
      this.update();
    }
    disconnectedCallback() { this.ro?.disconnect(); }
    step() {
      const item = this.rail.children[0];
      if (!item) return this.rail.clientWidth;
      const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      const per = Math.max(1, Math.floor((this.rail.clientWidth + gap) / (item.offsetWidth + gap)));
      return per * (item.offsetWidth + gap);
    }
    go(dir) { this.rail.scrollBy({ left: dir * this.step(), behavior: reduceMotion ? 'auto' : 'smooth' }); }
    update() {
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      const pos = this.rail.scrollLeft;
      const ratio = this.rail.scrollWidth ? this.rail.clientWidth / this.rail.scrollWidth : 1;
      if (this.bar) {
        const w = Math.min(100, ratio * 100);
        this.bar.style.setProperty('--w', `${w}%`);
        const x = max > 0 ? (pos / max) * ((100 - w) / w) * 100 : 0;
        this.bar.style.setProperty('--x', `${x}%`);
      }
      this.toggleAttribute('data-scrollable', max > 4);
      if (this.prev) this.prev.disabled = pos <= 4;
      if (this.next) this.next.disabled = pos >= max - 4;
    }
    drag() {
      let down = false; let sx = 0; let sl = 0; let moved = false;
      this.rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true; moved = false; sx = e.clientX; sl = this.rail.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - sx;
        if (Math.abs(dx) > 5 && !moved) { moved = true; this.rail.classList.add('is-dragging'); }
        if (moved) this.rail.scrollLeft = sl - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (moved) {
          this.rail.classList.remove('is-dragging');
          const s = this.step() / Math.max(1, Math.round(this.step() / (this.rail.children[0]?.offsetWidth || 1)));
          this.rail.scrollTo({ left: Math.round(this.rail.scrollLeft / s) * s, behavior: 'smooth' });
        }
      });
      this.rail.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    }
  });

  /* ---------- <eb-tabs>: category tabs with on-demand panels ---------- */
  define('eb-tabs', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.strip = this.querySelector('[data-tab-strip]');
      this.wrap = this.strip ? this.strip.parentElement : null;
      this.all = this.querySelector('[data-tabs-all]');
      this.cache = {};
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('pointerenter', () => this.fetchPanel(i), { passive: true });
        tab.addEventListener('focus', () => this.fetchPanel(i));
        tab.addEventListener('keydown', (e) => {
          const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
          if (!(e.key in keys)) return;
          e.preventDefault();
          let n = keys[e.key] === 'first' ? 0 : keys[e.key] === 'last' ? this.tabs.length - 1 : (i + keys[e.key] + this.tabs.length) % this.tabs.length;
          this.select(n); this.tabs[n].focus();
        });
      });
      if (this.strip) {
        this.querySelector('[data-strip-prev]')?.addEventListener('click', () => this.nudge(-1));
        this.querySelector('[data-strip-next]')?.addEventListener('click', () => this.nudge(1));
        this.strip.addEventListener('scroll', () => { if (!this.edgeRaf) this.edgeRaf = requestAnimationFrame(() => { this.edgeRaf = null; this.edges(); }); }, { passive: true });
        this.ro = new ResizeObserver(() => this.edges());
        this.ro.observe(this.strip);
      }
      // Warm the next couple of tabs once the page is idle.
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 2500));
      idle(() => { this.fetchPanel(1); this.fetchPanel(2); }, { timeout: 5000 });
    }
    disconnectedCallback() { this.ro?.disconnect(); }
    panelOf(i) { const t = this.tabs[i]; return t ? document.getElementById(t.getAttribute('aria-controls')) : null; }
    nudge(dir) { this.strip.scrollBy({ left: dir * this.strip.clientWidth * 0.7, behavior: reduceMotion ? 'auto' : 'smooth' }); }
    edges() {
      if (!this.strip || !this.wrap) return;
      const max = this.strip.scrollWidth - this.strip.clientWidth;
      this.wrap.toggleAttribute('data-more-left', this.strip.scrollLeft > 4);
      this.wrap.toggleAttribute('data-more-right', this.strip.scrollLeft < max - 4);
    }
    fetchPanel(i) {
      const t = this.tabs[i]; const panel = this.panelOf(i);
      if (!t || !panel || !panel.hasAttribute('data-lazy') || !t.dataset.url) return Promise.resolve(null);
      const url = t.dataset.url;
      if (!this.cache[url]) {
        const sep = url.includes('?') ? '&' : '?';
        this.cache[url] = fetch(`${url}${sep}section_id=eb-products-rail`, { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.text() : null))
          .catch(() => null);
      }
      return this.cache[url];
    }
    async hydrate(i) {
      const panel = this.panelOf(i);
      if (!panel || !panel.hasAttribute('data-lazy') || panel.dataset.loading) return;
      panel.dataset.loading = '1';
      const html = await this.fetchPanel(i);
      const rail = panel.querySelector('.eb-rail');
      const limit = parseInt(panel.dataset.limit || '8', 10);
      const cards = html ? [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.eb-pcard')].slice(0, limit) : [];
      if (cards.length) {
        rail.replaceChildren(...cards.map((c) => document.importNode(c, true)));
      } else {
        const url = this.tabs[i].dataset.url;
        rail.innerHTML = `<p class="eb-prods__empty">${html === null ? 'We couldn\u2019t load these just now.' : 'New pieces are on the way.'} <a href="${url}">View the collection</a></p>`;
      }
      panel.removeAttribute('data-lazy');
      delete panel.dataset.loading;
      rail.scrollLeft = 0;
      if (window.EB) EB.init(panel);
      panel.querySelector('eb-slider')?.update?.();
    }
    select(i) {
      this.tabs.forEach((t, x) => {
        const on = x === i;
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
        const panel = this.panelOf(x);
        if (panel) panel.hidden = !on;
      });
      const tab = this.tabs[i];
      const panel = this.panelOf(i);
      if (panel && !panel.hasAttribute('data-lazy')) {
        panel.querySelectorAll('[data-eb-rv]').forEach((el) => el.classList.add('is-in'));
        const slider = panel.querySelector('eb-slider');
        if (slider?.rail) { slider.rail.scrollLeft = 0; slider.update(); }
      }
      this.hydrate(i);
      this.fetchPanel(i + 1);
      if (this.all && tab?.dataset.url) this.all.href = tab.dataset.url;
      if (this.strip && tab) {
        const left = tab.offsetLeft - (this.strip.clientWidth - tab.offsetWidth) / 2;
        this.strip.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }
  });

  /* ---------- <eb-configurator>: tap a fabric, the bed changes ---------- */
  define('eb-configurator', class extends HTMLElement {
    connectedCallback() {
      this.btns = [...this.querySelectorAll('[data-sw]')];
      this.layers = [...this.querySelectorAll('[data-layer]')];
      this.nameEls = this.querySelectorAll('[data-cfg-name]');
      this.priceEl = this.querySelector('[data-cfg-price]');
      this.linkEls = this.querySelectorAll('[data-cfg-link]');
      this.i = 0; this.touched = false; this.timer = null;
      this.btns.forEach((b, n) => {
        b.addEventListener('click', () => { this.touched = true; this.stop(); this.show(n); });
        if (this.dataset.hover === 'true') {
          b.addEventListener('mouseenter', () => { if (finePointer) { this.stop(); this.show(n); } });
        }
      });
      const speed = parseInt(this.dataset.autoplay || '0', 10);
      if (speed > 0 && !reduceMotion && this.layers.length > 1 && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver((en) => {
          en.forEach((e) => {
            if (e.isIntersecting && !this.touched && !this.timer) {
              this.timer = setInterval(() => this.show((this.i + 1) % this.layers.length), speed * 1000);
            } else if (!e.isIntersecting) this.stop();
          });
        }, { threshold: 0.35 });
        this.io.observe(this);
      }
    }
    disconnectedCallback() { this.stop(); this.io?.disconnect(); }
    stop() { clearInterval(this.timer); this.timer = null; }
    show(n) {
      this.i = n;
      this.layers.forEach((l, x) => l.classList.toggle('is-on', x === n));
      this.btns.forEach((b, x) => b.setAttribute('aria-pressed', x === n ? 'true' : 'false'));
      const b = this.btns[n];
      if (!b) return;
      this.nameEls.forEach((el) => { el.textContent = b.dataset.name; });
      if (this.priceEl && b.dataset.price) this.priceEl.textContent = b.dataset.price;
      if (b.dataset.link) this.linkEls.forEach((a) => { a.href = b.dataset.link; });
    }
  });

  /* ---------- <eb-finance>: drag the price, see the monthly ---------- */
  define('eb-finance', class extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('[data-range]');
      this.terms = [...this.querySelectorAll('[data-term]')];
      this.cur = this.dataset.currency || '£';
      this.apr = parseFloat(this.dataset.apr || '0');
      this.locale = this.dataset.locale || 'en-GB';
      const on = this.terms.find((t) => t.getAttribute('aria-pressed') === 'true') || this.terms[this.terms.length - 1];
      this.months = parseInt(on?.dataset.term || '24', 10);
      this.range?.addEventListener('input', () => this.calc());
      this.terms.forEach((b) => b.addEventListener('click', () => {
        this.terms.forEach((x) => x.setAttribute('aria-pressed', 'false'));
        b.setAttribute('aria-pressed', 'true');
        this.months = parseInt(b.dataset.term, 10);
        this.calc(true);
      }));
      this.calc();
    }
    money(n, dec = 2) { return this.cur + n.toLocaleString(this.locale, { minimumFractionDigits: dec, maximumFractionDigits: dec }); }
    set(sel, txt) { const el = this.querySelector(sel); if (el) el.textContent = txt; }
    calc(bump) {
      if (!this.range) return;
      const price = parseFloat(this.range.value);
      let per; let total = price;
      if (this.apr > 0) {
        const r = this.apr / 100 / 12;
        per = (price * r) / (1 - Math.pow(1 + r, -this.months));
        total = per * this.months;
      } else per = price / this.months;
      const min = parseFloat(this.range.min); const max = parseFloat(this.range.max);
      this.range.style.setProperty('--fill', `${((price - min) / (max - min)) * 100}%`);
      this.set('[data-price]', this.money(Math.round(price), 0));
      this.set('[data-cash]', this.money(Math.round(price), 0));
      this.set('[data-monthly]', this.money(per));
      this.set('[data-termout]', `${this.months} ${this.dataset.monthsWord || 'months'} · ${this.apr}% APR`);
      this.set('[data-interest]', this.money(Math.max(0, total - price)));
      this.set('[data-total]', this.money(total));
      this.range.setAttribute('aria-valuetext', `${this.money(Math.round(price), 0)}, ${this.money(per)} per month`);
      if (bump && !reduceMotion) {
        const m = this.querySelector('[data-monthly]');
        m?.animate([{ transform: 'translateY(6px)', opacity: 0.4 }, { transform: 'none', opacity: 1 }], { duration: 420, easing: 'cubic-bezier(.16,1,.3,1)' });
      }
    }
  });

  /* ---------- <eb-countdown>: cut-off timer + real delivery date ---------- */
  define('eb-countdown', class extends HTMLElement {
    connectedCallback() {
      this.cutH = parseInt(this.dataset.hour || '16', 10);
      this.cutM = parseInt(this.dataset.minute || '0', 10);
      this.lead = parseInt(this.dataset.lead || '9', 10);
      this.skip = this.dataset.skipWeekends === 'true';
      this.locale = this.dataset.locale || 'en-GB';
      this.paint();
      this.t = setInterval(() => this.paint(), 1000);
    }
    disconnectedCallback() { clearInterval(this.t); }
    isWorking(d) { return !this.skip || (d.getDay() !== 0 && d.getDay() !== 6); }
    nextCutoff(now) {
      const c = new Date(now);
      c.setHours(this.cutH, this.cutM, 0, 0);
      if (c <= now || !this.isWorking(c)) {
        do { c.setDate(c.getDate() + 1); } while (!this.isWorking(c));
      }
      return c;
    }
    paint() {
      const now = new Date();
      const cut = this.nextCutoff(now);
      const diff = Math.max(0, cut - now);
      const pad = (n) => String(n).padStart(2, '0');
      const h = Math.floor(diff / 3.6e6); const m = Math.floor((diff % 3.6e6) / 6e4); const s = Math.floor((diff % 6e4) / 1000);
      this.querySelectorAll('[data-h]').forEach((el) => { el.textContent = pad(h); });
      this.querySelectorAll('[data-m]').forEach((el) => { el.textContent = pad(m); });
      this.querySelectorAll('[data-s]').forEach((el) => { el.textContent = pad(s); });
      const d = new Date(cut);
      let days = this.lead;
      while (days > 0) { d.setDate(d.getDate() + 1); if (this.isWorking(d)) days--; }
      const txt = d.toLocaleDateString(this.locale, { weekday: 'long', day: 'numeric', month: 'long' });
      this.querySelectorAll('[data-deldate]').forEach((el) => { if (el.textContent !== txt) el.textContent = txt; });
      const ring = this.querySelector('[data-ring]');
      if (ring) ring.style.setProperty('--p', (1 - diff / 8.64e7).toFixed(4));
    }
  });

  /* ---------- <eb-hotspots>: tap points on an image ---------- */
  define('eb-hotspots', class extends HTMLElement {
    connectedCallback() {
      this.spots = [...this.querySelectorAll('[data-spot]')];
      this.items = [...this.querySelectorAll('[data-spot-item]')];
      this.spots.forEach((sp, i) => {
        // With hover-to-open on a mouse, the hover has already opened it, so a click must not toggle it shut.
        const hoverOpens = () => this.dataset.hover === 'true' && finePointer;
        sp.addEventListener('click', (e) => { e.stopPropagation(); this.open(sp.getAttribute('aria-expanded') === 'true' && !hoverOpens() ? -1 : i); });
        if (this.dataset.hover === 'true') {
          sp.addEventListener('mouseenter', () => { if (finePointer) this.open(i); });
        }
      });
      this.items.forEach((it, i) => it.addEventListener('click', () => this.open(i)));
      this.onDoc = (e) => { if (!this.contains(e.target)) this.open(-1); };
      document.addEventListener('click', this.onDoc);
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.open(-1); });
    }
    disconnectedCallback() { document.removeEventListener('click', this.onDoc); }
    open(i) {
      this.spots.forEach((s, x) => s.setAttribute('aria-expanded', x === i ? 'true' : 'false'));
      this.items.forEach((it, x) => it.classList.toggle('is-active', x === i));
      this.toggleAttribute('data-has-open', i > -1);
      const item = this.items[i];
      const list = item?.parentElement;
      if (item && list && list.scrollWidth > list.clientWidth) {
        const pad = parseFloat(getComputedStyle(list).paddingLeft) || 0;
        list.scrollTo({ left: item.offsetLeft - pad, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }
  });

  /* ---------- <eb-compare>: mobile rival switcher ---------- */
  define('eb-compare', class extends HTMLElement {
    connectedCallback() {
      this.btns = [...this.querySelectorAll('[data-rival]')];
      this.btns.forEach((b) => b.addEventListener('click', () => {
        this.btns.forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
        this.dataset.show = b.dataset.rival;
      }));
    }
  });

  /* ---------- <eb-desc>: turn a raw product description into a clean read ----------
     Progressive enhancement: the original HTML stays in place if anything fails.
     Lead paragraph, ticked feature grids (incl. "•" lines crammed into one item),
     real subheadings, long extra sections folded into accordions, and the
     "call us / email us" boilerplate turned into a help card with tap-to-call. */
  define('eb-desc', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      const run = () => { try { this.build(); } catch (e) { /* keep the original description */ } };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
      else run();
    }

    // Runs of image-only paragraphs (e.g. drawer layout diagrams) become a tidy card gallery.
    galleryize(scope) {
      const isBlank = (n) => n.nodeType === 1 && !n.querySelector('img') && !n.textContent.replace(/\u00a0/g, ' ').trim() && !n.querySelector('table, iframe, video');
      const imgsOf = (n) => {
        if (n.nodeType !== 1) return null;
        if (n.tagName === 'IMG') return [n];
        if (!/^(P|DIV|FIGURE|SPAN|A)$/.test(n.tagName) || n.textContent.replace(/\u00a0/g, ' ').trim()) return null;
        const imgs = [...n.querySelectorAll('img')].filter((i) => i.getAttribute('src'));
        return imgs.length ? imgs : null;
      };
      const containers = [scope, ...scope.querySelectorAll('.ebd-intro, .ebd-sec, .ebd-acc__body, section, div:not(.ebd-gallery):not(.ebd-table)')];
      containers.forEach((box) => {
        let run = []; let imgs = [];
        const flush = () => {
          if (imgs.length) {
            const g = document.createElement('div');
            g.className = imgs.length > 1 ? 'ebd-gallery' : 'ebd-gallery ebd-gallery--single';
            imgs.forEach((img) => {
              const f = document.createElement('figure');
              f.className = 'ebd-shot';
              img.removeAttribute('width'); img.removeAttribute('height'); img.loading = 'lazy';
              f.appendChild(img);
              g.appendChild(f);
            });
            run[0].before(g);
            run.forEach((n) => n.remove());
          }
          run = []; imgs = [];
        };
        [...box.children].forEach((n) => {
          const found = imgsOf(n);
          if (found) { run.push(n); imgs.push(...found); return; }
          if (imgs.length && isBlank(n)) { run.push(n); return; }
          flush();
        });
        flush();
      });
    }

    build() {
      const src = this.querySelector('.product-description') || this;
      if (!src.textContent.trim()) return;
      this.dataset.ready = '1';
      // Hand-designed descriptions (own CSS, scripts, styled layouts) are left exactly as written.
      if (src.querySelector('style, script, iframe') || src.querySelectorAll('[style]').length > 4) {
        src.classList.add('is-custom');
        src.querySelectorAll('table').forEach((t) => { if (!t.parentElement.classList.contains('ebd-table')) { const w = document.createElement('div'); w.className = 'ebd-table'; t.replaceWith(w); w.appendChild(t); } });
        try { this.galleryize(src); } catch (e) { /* keep as written */ }
        return;
      }
      const BULLET = /^\s*[•·▪●◦\-–—✓✔✅☑]+\s*/;
      const HELP = /01924|@everestbeds|live chat|call us|email us/i;
      const words = (s) => s.replace(/\s+/g, ' ').trim();
      const textOf = (e) => words(e.innerHTML.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&'));
      const strip = (el) => { el.querySelectorAll('[data-start],[data-end]').forEach((n) => { n.removeAttribute('data-start'); n.removeAttribute('data-end'); }); return el; };

      // Lines crammed into one element with <br> + bullets become separate items.
      const splitLines = (html) => html.split(/<br\s*\/?>/i).map((s) => s.trim()).filter((s) => s.replace(/<[^>]+>/g, '').trim());
      const toItems = (html) => splitLines(html).map((s) => s.replace(/^(\s|&nbsp;)*(<\/?(strong|b|span)[^>]*>)*\s*[•·▪●◦✓✔✅☑\-–—]+\s*/i, (m) => m.replace(/[•·▪●◦✓✔✅☑\-–—]+\s*$/, '')));

      const blocks = [];
      const push = (kind, el, text) => blocks.push({ kind, el, text: text ?? words(el.textContent) });
      [...src.childNodes].forEach((n) => {
        if (n.nodeType === 3) { if (n.textContent.trim()) { const p = document.createElement('p'); p.textContent = n.textContent.trim(); push('p', p); } return; }
        if (n.nodeType !== 1) return;
        const el = strip(n.cloneNode(true));
        const tag = el.tagName;
        const text = textOf(el);
        if (!text && !el.querySelector('img, iframe, video, table')) return;
        if (/^H[1-6]$/.test(tag)) return push('h', el, text.replace(/:$/, ''));
        if (tag === 'UL' || tag === 'OL') {
          const items = [];
          el.querySelectorAll(':scope > li').forEach((li) => {
            if (/<br\s*\/?>/i.test(li.innerHTML) && (li.innerHTML.match(/<br\s*\/?>/gi) || []).length >= 1 && /[•·▪●✓✔✅]/.test(li.textContent)) items.push(...toItems(li.innerHTML));
            else items.push(li.innerHTML.trim().replace(BULLET, ''));
          });
          push('list', el, text); blocks[blocks.length - 1].items = items; return;
        }
        if ((tag === 'DIV' || tag === 'SECTION') && (el.className || el.querySelector('table, section, h1, h2, h3, h4, h5, h6, ul, ol'))) return push('raw', el, text);
        if (tag === 'P' || tag === 'DIV') {
          const html = el.innerHTML;
          const lines = splitLines(html);
          // A paragraph that is really a bullet list
          if (lines.length >= 2 && lines.filter((l) => BULLET.test(l.replace(/<[^>]+>/g, ''))).length >= Math.max(2, lines.length - 1)) {
            push('list', el, text); blocks[blocks.length - 1].items = toItems(html); return;
          }
          const onlyStrong = el.children.length === 1 && /^(STRONG|B)$/.test(el.children[0].tagName) && words(el.children[0].textContent) === text;
          if (text.length <= 64 && (/:$/.test(text) || onlyStrong) && !/[.!?]$/.test(text.replace(/:$/, ''))) return push('h', el, text.replace(/:$/, ''));
          if (HELP.test(text)) return push('help', el, text);
          return push('p', el, text);
        }
        push('raw', el, text);
      });
      if (!blocks.length) return;

      // A short title-like first line ("Ottoman Divan Bed") becomes a kicker, not a section.
      if (blocks[0].kind === 'p' && blocks[0].text.length < 40 && !/[.!?:]$/.test(blocks[0].text) && blocks[1] && blocks[1].kind === 'p') blocks[0].kind = 'kicker';

      // Group into an intro + titled sections; help text is pulled out into one card.
      const intro = []; const sections = []; const help = [];
      let cur = null;
      blocks.forEach((b) => {
        if (b.kind === 'help') { help.push(b.text); return; }
        if (b.kind === 'h') { cur = { title: b.text, items: [] }; sections.push(cur); return; }
        (cur ? cur.items : intro).push(b);
      });

      const el = (tag, cls, html) => { const e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; };
      const renderList = (items) => {
        const all = items.map((s) => s.trim()).filter((s) => s.replace(/<[^>]+>/g, '').trim() || /<(table|img)/i.test(s));
        const withTable = all.filter((s) => /<table/i.test(s));
        const clean = all.filter((s) => !/<table/i.test(s));
        if (withTable.length) {
          const box = el('div', 'ebd-listwrap');
          if (clean.length) box.appendChild(renderList(clean));
          withTable.forEach((html) => { const w = el('div', 'ebd-table'); w.innerHTML = html; box.appendChild(w); });
          return box;
        }
        const len = (s) => s.replace(/<[^>]+>/g, '').length;
        const shortCount = clean.filter((s) => len(s) <= 52).length;
        const grid = clean.length > 3 && shortCount / clean.length >= 0.7;
        const ul = el('ul', 'ebd-list' + (grid ? ' ebd-list--grid' : ''));
        clean.forEach((s, i) => {
          const note = /^\s*(<[^>]+>\s*)*note\s*:/i.test(s);
          const li = el('li', (note ? 'ebd-note' : '') + (grid && !note && len(s) > 52 ? ' ebd-wide' : ''), s);
          li.style.setProperty('--i', i);
          ul.appendChild(li);
        });
        // Long lists show their first six, with a button for the rest.
        if (clean.length > 8) {
          ul.classList.add('ebd-list--clip');
          const wrap = el('div', 'ebd-listwrap');
          const btn = el('button', 'ebd-more', `Show all ${clean.length}`);
          btn.type = 'button';
          btn.setAttribute('aria-expanded', 'false');
          btn.addEventListener('click', () => {
            const open = ul.classList.toggle('is-open');
            btn.setAttribute('aria-expanded', open ? 'true' : 'false');
            btn.textContent = open ? 'Show fewer' : `Show all ${clean.length}`;
          });
          wrap.append(ul, btn);
          return wrap;
        }
        return ul;
      };
      const renderBlocks = (list, target, leadFirst) => {
        let leadDone = !leadFirst;
        list.forEach((b) => {
          if (b.kind === 'list') return target.appendChild(renderList(b.items || []));
          if (b.kind === 'kicker') return target.appendChild(el('p', 'ebd-kicker', b.el.innerHTML));
          if (b.kind === 'sub') return target.appendChild(el('p', 'ebd-sub', b.text));
          if (b.kind === 'p' && !leadDone) { leadDone = true; return target.appendChild(el('p', 'ebd-lede', b.el.innerHTML)); }
          if (b.kind === 'p') return target.appendChild(el('p', '', b.el.innerHTML));
          if (b.el.tagName === 'TABLE' || b.el.querySelector('table')) { const w = el('div', 'ebd-table'); w.appendChild(b.el); return target.appendChild(w); }
          target.appendChild(b.el);
        });
      };

      const out = el('div', 'ebd');
      if (intro.length) { const sec = el('div', 'ebd-intro'); renderBlocks(intro, sec, true); out.appendChild(sec); }

      const KEEP_OPEN = /feature|spec|detail|dimension|size|key|overview|why you/i;
      const GENERIC = /^(key )?(spec(ification)?s?|features?|key features)$/i;
      // Decide open/folded first; generic "Specifications" right after a folded
      // section (e.g. a mattress option) belongs inside that section's card.
      let openCount = 0;
      const plan = [];
      sections.forEach((s) => {
        if (!s.items.length) return;
        const prev = plan[plan.length - 1];
        if (prev && !prev.open && GENERIC.test(s.title)) { prev.items.push({ kind: 'sub', text: s.title }, ...s.items); return; }
        const hasMedia = s.items.some((b) => b.kind === 'raw' || (b.el && b.el.querySelector && b.el.querySelector('img, table')) || (b.items && b.items.some((h) => /<(table|img)/i.test(h))));
        const open = KEEP_OPEN.test(s.title) || hasMedia || openCount < 1;
        if (open) openCount++;
        plan.push({ title: s.title, items: s.items, open });
      });
      plan.forEach((s, i) => {
        const open = s.open;
        const n = String(i + 1).padStart(2, '0');
        if (open) {
          const sec = el('section', 'ebd-sec');
          sec.appendChild(el('h3', 'ebd-h', `<span class="ebd-h__n">${n}</span><span>${s.title}</span>`));
          renderBlocks(s.items, sec, false);
          out.appendChild(sec);
        } else {
          const d = el('details', 'ebd-acc');
          const firstText = s.items.find((b) => b.kind === 'p');
          const hint = firstText ? firstText.text.split(/(?<=[.!?])\s/)[0] : '';
          d.appendChild(el('summary', '', `<span class="ebd-acc__t"><span class="ebd-h__n">${n}</span><span><strong>${s.title}</strong>${hint && hint.length < 120 ? `<small>${hint}</small>` : ''}</span></span><span class="ebd-acc__ico" aria-hidden="true"></span>`));
          const body = el('div', 'ebd-acc__body');
          renderBlocks(s.items, body, false);
          d.appendChild(body);
          out.appendChild(d);
        }
      });

      if (help.length) {
        const all = help.join(' ');
        const phone = (all.match(/0\d{3,4}\s?\d{3}\s?\d{3,4}/) || [])[0];
        const email = (all.match(/[\w.+-]+@[\w-]+\.[\w.-]+/) || [])[0];
        const card = el('aside', 'ebd-help');
        const ico = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.5 12a8 8 0 0 1-11.7 7.1L3.5 20.5l1.4-4.9A8 8 0 1 1 20.5 12z"/></svg>';
        const acts = [
          phone ? `<a class="ebd-help__btn" href="tel:${phone.replace(/\s/g, '')}">Call ${phone}</a>` : '',
          email ? `<a class="ebd-help__btn ebd-help__btn--ghost" href="mailto:${email.replace(/\.$/, '')}">Email us</a>` : '',
        ].join('');
        // Keep the friendly sentences; contact details become buttons instead.
        const CONTACT = /\d{5}|@|\bcall\b|\bemail\b|contact|live chat/i;
        const keep = [];
        all.split(/(?<=[.!?])\s+/).forEach((sen) => {
          if (!CONTACT.test(sen)) { keep.push(sen.trim()); return; }
          const head = sen.split(',')[0].trim();
          if (head.length > 12 && !CONTACT.test(head)) keep.push(head.replace(/[.,;]$/, '') + '.');
        });
        const body = keep.join(' ') || 'Our team can help with sizes, fabrics and custom requests.';
        card.innerHTML = `<span class="ebd-help__ico">${ico}</span><div class="ebd-help__txt"><strong>${this.dataset.helpTitle || 'Want it made your way?'}</strong><p>${body}</p>${acts ? `<div class="ebd-help__acts">${acts}</div>` : ''}</div>`;
        out.appendChild(card);
      }

      try { this.galleryize(out); } catch (e) { /* keep as built */ }
      src.replaceChildren(out);
      src.classList.add('is-enhanced');
      if (!reduceMotion && 'IntersectionObserver' in window && root.classList.contains('eb-motion')) {
        const io = new IntersectionObserver((en) => en.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -6% 0px' });
        out.querySelectorAll(':scope > *').forEach((c, i) => { c.classList.add('ebd-rv'); c.style.setProperty('--d', `${Math.min(i, 6) * 70}ms`); io.observe(c); });
      }
    }
  });

  /* ---------- <eb-timeline>: draw progress line when in view ---------- */
  define('eb-timeline', class extends HTMLElement {
    connectedCallback() {
      if (reduceMotion || !('IntersectionObserver' in window)) { this.classList.add('is-lit'); return; }
      this.io = new IntersectionObserver((en) => {
        en.forEach((e) => { if (e.isIntersecting) { this.classList.add('is-lit'); this.io.disconnect(); } });
      }, { threshold: 0.25 });
      this.io.observe(this);
    }
    disconnectedCallback() { this.io?.disconnect(); }
  });

  /* ---------- <eb-header-skin>: glass state once the page scrolls ---------- */
  define('eb-header-skin', class extends HTMLElement {
    connectedCallback() {
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:24px;pointer-events:none;';
      document.body.prepend(sentinel);
      this.sentinel = sentinel;
      this.io = new IntersectionObserver(([e]) => {
        document.documentElement.classList.toggle('eb-scrolled', !e.isIntersecting);
      });
      this.io.observe(sentinel);
    }
    disconnectedCallback() { this.io?.disconnect(); this.sentinel?.remove(); }
  });
})();
